import type { Filters } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import {
  type ApiTableAutocompleteHook,
  useApiTableAutocomplete,
} from 'components/FormikTypeaheadInput/useApiTableAutocomplete';
import { isValidIpv4Address } from 'utils/formValidation/validations';
import { useLicenseHasValidAim } from 'utils/hooks/useLicenseHasValidAim';
import { useGlobalStateContextSelectors } from '../../../GlobalState/GlobalStateModel';
import type { GraphPathLookupIpOption } from './GraphPathLookupIpOption';

interface Props {
  excludeIps?: string[];
  additionalFilters?: Filters[];
  url: string;
}

export const usePathLookupIpAutocomplete = (
  props: Props,
): ApiTableAutocompleteHook<GraphPathLookupIpOption> => {
  const globalCtxProps = useGlobalStateContextSelectors({
    attributeFilters: prop('attributeFilters'),
  });
  const hasValidAim = useLicenseHasValidAim();

  return useApiTableAutocomplete({
    snapshot: true,
    debounced: true,
    createRequest: (searchEvent) => {
      // If IP is CIDR, use only IP address for 'ip' filter.
      // Fixed noUncheckedIndexedAccess NIM-19408
      const ipSearchValue = searchEvent.inputValue.split('/')[0]!;
      if (ipSearchValue === '') {
        // this means input starts with '/', that should result in
        // no results rather than matching everything by ['like' ''].
        return 'invalid';
      }
      // cannot be inlined in the return value, fires some Typescript false positive
      const aimSearchFilters: Filters[] = hasValidAim
        ? [
            { aimWorkloadName: ['like', searchEvent.inputValue] },
            { aimApplicationName: ['like', searchEvent.inputValue] },
          ]
        : [];
      let filters: Filters = {
        and: [
          {
            or: [
              { ip: ['like', ipSearchValue] },
              { dnsName: ['like', searchEvent.inputValue] },
              ...aimSearchFilters,
            ],
          },
          ...(props.additionalFilters ?? []),
        ],
      };
      const actualExcludeIps =
        props.excludeIps?.filter(isValidIpv4Address) ?? [];
      if (actualExcludeIps.length > 0) {
        filters = {
          and: [
            filters,
            ...actualExcludeIps.map(
              // Fixed noUncheckedIndexedAccess NIM-19408
              (ip): Filters => ({ ip: ['neq', ip.split('/')[0]!] }),
            ),
          ],
        };
      }
      return {
        url: props.url,
        data: {
          columns: [
            'id',
            'ip',
            'dnsName',
            ...(hasValidAim
              ? (['aimWorkloadName', 'aimApplicationName'] as const)
              : []),
          ],
          filters,
          attributeFilters: globalCtxProps.attributeFilters,
        },
      };
    },
  });
};
